import React, { useCallback, useEffect, useRef, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Send, Loader2, Sparkles, Phone } from 'lucide-react';
import { askAssistant, checkAssistantAvailable, AssistantMessage } from '../../services/orders.service';

const GREETING: AssistantMessage = {
  role: 'assistant',
  content:
    "Hi! I can help you find the right compression stockings or braces — by use, compression level, size, or style. What are you shopping for?",
};

const SUGGESTIONS = [
  'Which compression level for tired legs?',
  'Knee-high vs thigh-high?',
  "I'm on my feet all day — what do you suggest?",
];

export function AssistantWidget() {
  const [available, setAvailable] = useState(false);
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState<AssistantMessage[]>([GREETING]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const scrollRef = useRef<HTMLDivElement>(null);

  // Only show the assistant if the backend has it configured
  useEffect(() => {
    checkAssistantAvailable().then(setAvailable);
  }, []);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: 'smooth' });
  }, [messages, loading]);

  const send = useCallback(async (text: string) => {
    const trimmed = text.trim();
    if (!trimmed || loading) return;
    const next = [...messages, { role: 'user' as const, content: trimmed }];
    setMessages(next);
    setInput('');
    setLoading(true);
    try {
      // Send recent history (cap to keep requests small)
      const reply = await askAssistant(next.slice(-12));
      setMessages((m) => [...m, { role: 'assistant', content: reply }]);
    } catch (err: any) {
      const status = err?.response?.status;
      const msg =
        status === 429
          ? "You're chatting fast! Give me a few seconds and try again."
          : "Sorry — I hit a snag. Please try again, or call us at 306-979-4115.";
      setMessages((m) => [...m, { role: 'assistant', content: msg }]);
    } finally {
      setLoading(false);
    }
  }, [messages, loading]);

  if (!available) return null;

  return (
    <>
      {/* Launcher */}
      <AnimatePresence>
        {!open && (
          <motion.button
            initial={{ opacity: 0, scale: 0.8, y: 10 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.8, y: 10 }}
            onClick={() => setOpen(true)}
            className="fixed bottom-5 right-5 z-[55] flex items-center gap-2 pl-4 pr-5 py-3 rounded-full bg-ndms-navy text-white shadow-xl hover:bg-[#7B68CC] transition-colors"
            aria-label="Open shopping assistant"
          >
            <Sparkles size={18} className="fill-white/30" />
            <span className="text-sm font-bold">Ask us</span>
          </motion.button>
        )}
      </AnimatePresence>

      {/* Panel */}
      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: 24, scale: 0.98 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 24, scale: 0.98 }}
            transition={{ duration: 0.2 }}
            className="fixed bottom-0 right-0 sm:bottom-5 sm:right-5 z-[55] w-full sm:w-[380px] h-[85vh] sm:h-[560px] bg-white sm:rounded-3xl rounded-t-3xl shadow-2xl border border-gray-100 flex flex-col overflow-hidden"
          >
            {/* Header */}
            <div className="bg-ndms-navy text-white px-4 py-3.5 flex items-center justify-between flex-shrink-0">
              <div className="flex items-center gap-2.5">
                <div className="w-8 h-8 rounded-full bg-white/15 flex items-center justify-center">
                  <Sparkles size={15} className="fill-white/40" />
                </div>
                <div>
                  <p className="text-sm font-bold leading-tight">Shopping Assistant</p>
                  <p className="text-[11px] text-white/60 leading-tight">Northdale Medical Supplies</p>
                </div>
              </div>
              <button onClick={() => setOpen(false)} className="p-1.5 hover:bg-white/10 rounded-lg transition-colors" aria-label="Close">
                <X size={18} />
              </button>
            </div>

            {/* Messages */}
            <div ref={scrollRef} className="flex-1 overflow-y-auto scrollbar-thin px-4 py-4 space-y-3 bg-gray-50">
              {messages.map((m, i) => (
                <div key={i} className={`flex ${m.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                  <div
                    className={`max-w-[85%] px-3.5 py-2.5 rounded-2xl text-sm leading-relaxed whitespace-pre-wrap ${
                      m.role === 'user'
                        ? 'bg-ndms-navy text-white rounded-br-md'
                        : 'bg-white text-gray-700 border border-gray-100 rounded-bl-md shadow-sm'
                    }`}
                  >
                    {m.content}
                  </div>
                </div>
              ))}

              {/* Starter suggestions (only before the first user turn) */}
              {messages.length === 1 && !loading && (
                <div className="flex flex-wrap gap-2 pt-1">
                  {SUGGESTIONS.map((s) => (
                    <button
                      key={s}
                      onClick={() => send(s)}
                      className="text-xs text-ndms-navy bg-white border border-gray-200 rounded-full px-3 py-1.5 hover:border-ndms-navy transition-colors text-left"
                    >
                      {s}
                    </button>
                  ))}
                </div>
              )}

              {loading && (
                <div className="flex justify-start">
                  <div className="bg-white border border-gray-100 rounded-2xl rounded-bl-md px-3.5 py-3 shadow-sm">
                    <Loader2 size={16} className="animate-spin text-[#7B68CC]" />
                  </div>
                </div>
              )}
            </div>

            {/* Disclaimer + input */}
            <div className="flex-shrink-0 border-t border-gray-100 bg-white">
              <p className="text-[10px] text-gray-400 leading-snug px-4 pt-2 flex items-center gap-1">
                <Phone size={9} /> General product help only — for medical advice, call 306-979-4115.
              </p>
              <form
                onSubmit={(e) => { e.preventDefault(); send(input); }}
                className="flex items-center gap-2 px-3 py-3"
              >
                <input
                  type="text"
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  placeholder="Ask about a product…"
                  maxLength={2000}
                  className="flex-1 px-3.5 py-2.5 rounded-full border border-gray-200 text-sm focus:outline-none focus:border-ndms-navy focus:ring-1 focus:ring-ndms-navy/20"
                />
                <button
                  type="submit"
                  disabled={loading || !input.trim()}
                  className="w-10 h-10 rounded-full bg-ndms-navy text-white flex items-center justify-center hover:bg-[#7B68CC] transition-colors disabled:opacity-40 flex-shrink-0"
                  aria-label="Send"
                >
                  <Send size={16} />
                </button>
              </form>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
